import type { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { ArrowDownIcon, MapPinIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

type Address = components["schemas"]["Address"];

interface RouteWaypointProps {
  label: string;
  name: string | null;
  address?: Address | null;
  isCurrent?: boolean;
  isIntermediate?: boolean;
}

export function RouteWaypoint({ label, name, address, isCurrent, isIntermediate }: RouteWaypointProps) {
  const { t } = useTranslation(["fit"]);
  const streetLine = [address?.street, address?.houseNumber].filter(Boolean).join(" ").trim();
  const cityLine = [address?.postalCode, address?.city].filter(Boolean).join(" ").trim();

  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isCurrent ? "bg-primary/10" : "bg-secondary",
        )}
      >
        {isIntermediate ? (
          <ArrowDownIcon className="text-primary h-4 w-4" />
        ) : (
          <MapPinIcon className={cn("h-4 w-4", isCurrent ? "text-primary" : "text-foreground")} />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-muted-foreground text-xs font-medium">
          {label}
          {isCurrent && !isIntermediate && <span className="text-primary"> ({t("fit:Current")})</span>}
        </div>
        {name ? (
          <>
            <div className="font-medium">{name}</div>
            {(streetLine || cityLine) && (
              <div className="text-muted-foreground mt-0.5 text-xs">
                {streetLine && <span>{streetLine}</span>}
                {streetLine && cityLine && <span>, </span>}
                {cityLine && <span>{cityLine}</span>}
              </div>
            )}
          </>
        ) : (
          <span className="text-muted-foreground text-sm italic">{t("fit:NoData")}</span>
        )}
      </div>
    </div>
  );
}
